import React from 'react';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { SurveyType } from '../../../../../shared/constants';
import {
  SURVEY_RESPONSE_BY_TYPE,
  SURVEY_SCALE_BY_TYPE,
} from '../../../../../shared/surveys';
import {
  dimGrey,
  inactive,
  lightBackground1,
  lightBlack,
  lightGrey,
  lightPurple,
  outlineSeparator,
} from '../../../../../mainTheme';
import type { SurveyInsightsPeriod } from '../../../../../store/hubOwner/types';
import {
  buildTrendAxisTicks,
  CHART_HEIGHT,
  formatBucketRange,
  localDayMs,
  SurveyTrendPoint,
} from './surveyInsights';

interface SurveyTrendChartProps {
  points: SurveyTrendPoint[];
  surveyType: SurveyType;
  // Resolved window from the API; the x axis spans it even where buckets
  // at the edges have no responses.
  period?: SurveyInsightsPeriod | null;
  // Mean score drawn as a dashed reference line; omitted when null, and
  // when there is a single answered bucket (the line would sit on the dot
  // and its label on top of both).
  average?: number | null;
  height?: number;
}

const formatTick = (ts: number) => moment(ts).format('MMM D');

const TrendTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const point: SurveyTrendPoint = payload[0].payload;
  return (
    <Box
      sx={{
        background: lightBackground1,
        border: `1px solid ${outlineSeparator}`,
        borderRadius: '8px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Typography sx={{ color: dimGrey, fontSize: '12px', fontWeight: 500 }}>
        {formatBucketRange(point)}
      </Typography>
      <Typography sx={{ color: lightBlack, fontSize: '12px', fontWeight: 700 }}>
        Average {point.average}
      </Typography>
      <Typography sx={{ color: dimGrey, fontSize: '12px', fontWeight: 500 }}>
        {point.count} answered day{point.count === 1 ? '' : 's'}
      </Typography>
    </Box>
  );
};

// Average score per answered bucket over the window for one survey type.
// Single series, so no legend: the panel title names it. The y axis is the
// survey's own scale, labelled with the response text rather than the raw
// numbers. The line runs between answered buckets; the x axis still spans the
// whole window, ticked by calendar day rather than by data point, so the
// reader sees when the patient did not answer even with one answered bucket.
const SurveyTrendChart: React.FC<SurveyTrendChartProps> = ({
  points,
  surveyType,
  period,
  average,
  height = CHART_HEIGHT,
}) => {
  const { min, max } = SURVEY_SCALE_BY_TYPE[surveyType];
  const labels = SURVEY_RESPONSE_BY_TYPE[surveyType];
  const ticks = Array.from({ length: max - min + 1 }, (_, i) => min + i);

  const from = period ? localDayMs(period.from) : NaN;
  const to = period ? localDayMs(period.to) : NaN;
  const xDomain: [number | string, number | string] =
    Number.isFinite(from) && Number.isFinite(to) && from < to
      ? [from, to]
      : ['dataMin', 'dataMax'];
  const xTicks = buildTrendAxisTicks(period);
  const showAverage = average != null && points.length > 1;

  return (
    <Box
      sx={{ width: '100%', height }}
      role="img"
      aria-label="Survey score trend"
      data-testid="survey-trend-chart"
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={points}
          margin={{ top: 12, right: 24, left: 0, bottom: 0 }}
        >
          <CartesianGrid vertical={false} stroke={inactive} />
          <XAxis
            dataKey="ts"
            type="number"
            scale="time"
            domain={xDomain}
            ticks={xTicks}
            interval="preserveStartEnd"
            tickFormatter={formatTick}
            tick={{ fontSize: 11, fill: dimGrey }}
            axisLine={{ stroke: outlineSeparator }}
            tickLine={false}
            padding={{ left: 16, right: 16 }}
            minTickGap={24}
          />
          <YAxis
            dataKey="average"
            type="number"
            domain={[min, max]}
            ticks={ticks}
            tickFormatter={(score: number) => labels[score] ?? String(score)}
            tick={{ fontSize: 11, fill: dimGrey }}
            axisLine={false}
            tickLine={false}
            width={120}
            allowDecimals={false}
          />
          <Tooltip
            content={<TrendTooltip />}
            cursor={{ stroke: outlineSeparator }}
          />
          {showAverage && (
            <ReferenceLine
              y={average}
              stroke={lightGrey}
              strokeDasharray="4 4"
              ifOverflow="extendDomain"
              label={{
                value: `Avg ${average}`,
                position: 'insideTopRight',
                fontSize: 11,
                fill: dimGrey,
              }}
            />
          )}
          <Line
            type="linear"
            dataKey="average"
            name="Average score"
            stroke={lightPurple}
            strokeWidth={2}
            dot={{
              r: 4,
              fill: lightPurple,
              stroke: lightBackground1,
              strokeWidth: 2,
            }}
            activeDot={{
              r: 6,
              fill: lightPurple,
              stroke: lightBackground1,
              strokeWidth: 2,
            }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default SurveyTrendChart;
